import React, { useState, useCallback, useEffect } from 'react';
import { FaPlus, FaMinus } from 'react-icons/fa';
import { createUseStyles } from 'react-jss';
import { useDispatch, useSelector } from 'react-redux';
import clsx from 'clsx';
import shortid from 'shortid';
import Button from './Button';
import Modal, { ModalProps } from 'react-bootstrap/Modal';

import { Twig, Product, ProductVariant, CartItem } from '../types';
import Text from './Text';
import { findMatchingVariant } from '../lib/utils';
import { addCartItem, removeCartItem } from '../actions/cart';
import { RootState } from '../config/store';
import { track } from '../services/mixpanelService';
import { toggleContinueShoppingModal } from '../actions/ui';

export interface AddToCartModalProps extends ModalProps {
  twig?: Twig;
  product: Product;
  type?: 'add' | 'edit';
  item?: any;
  onClose: () => void;
}
const AddToCartModal: React.FC<AddToCartModalProps> = ({
  twig,
  product,
  // type = "edit",
  // item,
  onClose,
  ...others
}) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const [quantity, setQuantity] = useState(1);
  const [item, setItem] = useState<null | CartItem>(null);
  const [foundInCart, setFoundInCart] = useState(false);
  const hasOptions = Object.keys(product.options).length > 0;
  const cart = useSelector<RootState, CartItem[]>((state) => state.cart);

  const [type, setType] = useState(() => {
    const exists = cart.find(
      (item) =>
        item.product.id === product.id
    );
    if (exists) {
      setItem(exists)
      setQuantity(exists.quantity)
      return "edit"
    } else {
      return "add"
    }
  });
  const [selectedOptions, setSelectedOptions] = useState<
    Record<string, number>
  >(() => {
    const ret: Record<string, number> = {};
    if (type == 'edit' && item && item.variant) {
      for (let index = 0; index < item.variant.optionValues.length; index++) {
        const element = item.variant.optionValues[index];
        ret[element.name] = element.id;        
      }
    }
    else {
      console.log(product.overSellEnabled);
      Object.keys(product.options).forEach((key) => {
      ret[key] = product.options[key][0].id;
      });
    }
    return ret;
  });
  const handleSelectOption = useCallback(
    ({ target: { name, value } }) => { 
           
      setQuantity(0);
      setSelectedOptions({
        ...selectedOptions,
        [name]: parseInt(value, 10),
      });

    },
    [selectedOptions],
  );

  const matchingVariant = hasOptions
    ? findMatchingVariant(product.variants, selectedOptions)
    : null;

  let totalQty = 0;
  if (hasOptions) {
    if (matchingVariant) {
      totalQty = matchingVariant.quantity;
    }
  } else {
    totalQty = product.totalQuantity;
  }

  const handleIncreamentQty = useCallback(() => {
    setQuantity(quantity + 1);
  }, [quantity]);

  const handleDecreamentQty = useCallback(() => {
    setQuantity(quantity - 1);
  }, [quantity]);

  const handleAddToCart = useCallback(() => {
    if (hasOptions && !matchingVariant) return;
    if (type == 'add') {
      track("add_to_cart")
      const exists = cart.find(
        (item) =>
          item.product.id === product.id &&
          item.variant?.id === matchingVariant?.id,
      );
      if (exists) {
        setFoundInCart(true);
        return
      }
    }
    const newItem: CartItem = {
      id: shortid.generate(),
      twig,
      product,
      variant: hasOptions ? matchingVariant : null,
      quantity,
    };
    if (type == 'edit' && item) {
      dispatch(removeCartItem(item.id));
      if (quantity == 0) {
        track("remove_from_cart")
      }
    }
    if (quantity != 0) {
      dispatch(addCartItem(newItem));
      setType('edit');
      setItem(newItem);
    } else {
      setType('add');
      setItem(null);
    }
    onClose();
    dispatch(toggleContinueShoppingModal(true));
  }, [dispatch, onClose, matchingVariant, twig, product, quantity, hasOptions]);
  useEffect(() => {
    if (!product.overSellEnabled)
      if (hasOptions) {
        if (!matchingVariant) {
          setQuantity(0);
        } else {
          setQuantity(Math.max(0, Math.min(quantity, totalQty)));
        }
      } else {
        setQuantity(Math.max(0, Math.min(quantity, totalQty)));
      }
  }, [matchingVariant, quantity, hasOptions, totalQty]);

  return (
    <>
      <Modal {...others}>
        <Modal.Body>
          <div>
            <Text as="h4" align="center" capitalized>
              {type == 'add' ? 'Add to cart' : 'Edit Item'}
            </Text>
          </div>
          <div className="mb-4">
            <Text className="mb-1" subText>
              Quantity
            </Text>
            <div className={classes.qtyBox}>
              <button
                className={classes.qtyBtn}
                type="button"
                onClick={handleDecreamentQty}
                disabled={(hasOptions && !matchingVariant) || quantity <= 0}
              >
                <FaMinus />
              </button>
              <div className={classes.qtyText}>{quantity}</div>
              <button
                className={classes.qtyBtn}
                type="button"
                onClick={handleIncreamentQty}
                disabled={
                  product.overSellEnabled == false && ((hasOptions && !matchingVariant) || quantity >= totalQty)
                }
              >
                <FaPlus />
              </button>
            </div>
          </div>
          <div>
            
            {Object.keys(product.options).map((key) => (
              <div key={key}>
                <Text className="mb-1" subText capitalized bold>
                  {key}:
                </Text>
                <div className={classes.optionBox}>
                  
                  {product.options[key].map((option) => (
                    <div
                      className={clsx(classes.option, {
                        [classes.optionLabelSelected]:
                          selectedOptions[key] === option.id,
                      })}
                      key={option.id}
                    >
                      <input
                        className={classes.optionInput}
                        id={`option-${option.id}`}
                        type="radio"
                        value={option.id}
                        name={key}
                        checked={selectedOptions[key] === option.id}
                        onChange={handleSelectOption}
                      />
                      
                      <label
                        className={classes.optionLabel}
                        htmlFor={`option-${option.id}`}
                      >
                        {key.toLowerCase() === 'color' ? (
                          <div
                            className={classes.options}
                            style={{
                                backgroundColor: '#'+option.value,
                            }}
                          />
                        ) : (
                          <Text as="span" uppercase>
                            {option.value}
                          </Text>
                        )}
                      </label>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
          {hasOptions && !matchingVariant && !product.overSellEnabled ? (
            <div>
              <Text className={classes.errorText} align="center">
                Selected options are not available at the moment
              </Text>
            </div>
          ) : null}
          {foundInCart ? (
            <div>
              <Text className={classes.errorText} align="center">
                Item already exist in cart
              </Text>
            </div>) : null
          }
          <div>
            <Button
              onClick={handleAddToCart}
              disabled={hasOptions && !matchingVariant&& !product.overSellEnabled}
              rounded
              block
            >
              {type == 'add' ? 'Add to cart' : 'Update item'}
            </Button>
            {/* <Button>Proceed to checkout</Button> */}
          </div>
        </Modal.Body>
      </Modal>
    </>
  );
};

const useStyles = createUseStyles({
  optionBox: {
    display: 'flex',
    justifyContent: 'flex-start',
    flexWrap: 'wrap',
    marginBottom: '15px',
  },
  options:{
    width: 20,
    height: 20,
    borderRadius: '50%',
  },
  option: {
    marginRight: '10px',
    marginBottom: '15px',
    padding: '5px 15px',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    boxShadow: 'rgb(0 0 0 / 39%) 0px 0px 2px',
    borderRadius: '30px',
    cursor: 'pointer',
    '&:hover': {
      border: "1px solid #522583",
    },
  },
  optionInput: {
    display: 'none',
  },
  optionLabel: {
    margin: '0',
    cursor: 'pointer',
  },
  optionLabelSelected: {
    // backgroundColor: '#eee',
    border: "1px solid #522583",
    '&:hover': {
      border: "1px solid #522583",
    },
  },
  qtyBox: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '7px',
    border: '1px solid #ddd',
    borderRadius: '30px',
  },
  qtyBtn: {
    border: 'none',
    background: '#eee',
    borderRadius: '50%',
    width: '32px',
    height: '32px',
    '&:focus': {
      outline: 'none',
    },
  },
  qtyText: {},
  errorText: {
    color: 'red',
  },
});

export default AddToCartModal;
